import React, { useMemo } from "react";
import cx from "classnames";

import {
  sortOutcomes,
  sortPairedOutcomes,
} from "@/utilities/sharedBettingUtilities";
import { useIsLiveViewOnly } from "@/features/betting/hooks/useIsLiveViewOnly";
import { Outcome } from "./Outcome";
import { LiftedTeamNames } from "./LiftedTeamNames";
import { getOutcomeCompetitor, type OutcomeLayoutProps } from "./outcomeLayout";

import type {
  ProcessedOutcome,
  ProcessedOutcomes,
} from "@/hooks/firestore/betting/useEvent";

import * as styles from "./Outcomes.module.scss";

type StackedOutcomesProps = OutcomeLayoutProps & {
  marketType?: string;
  fullWidth?: boolean;
};

const sortOutcomesForDisplay = (
  outcomes: ProcessedOutcomes,
  marketType: string,
  isOutright: boolean,
  isLiveViewOnly: boolean,
  showInactive: boolean,
): ProcessedOutcome[] => {
  if (
    marketType.includes("HANDICAP") &&
    !marketType.includes("HANDICAPTHREEWAY")
  ) {
    return sortPairedOutcomes(outcomes, ["HOME", "AWAY"], false, showInactive);
  }

  const outcomesEntries = Object.entries(outcomes || {});

  // handle OTHER outcomes with OVER/UNDER
  const pairedEntries = outcomesEntries.filter(
    ([_, outcome]) => outcome.type === "OVER" || outcome.type === "UNDER",
  );
  const otherEntries = outcomesEntries.filter(
    ([_, outcome]) => outcome.type !== "OVER" && outcome.type !== "UNDER",
  );

  if (pairedEntries.length > 0) {
    const sortedPairs = sortPairedOutcomes(
      Object.fromEntries(pairedEntries),
      ["OVER", "UNDER"],
      true,
      showInactive,
    );
    const sortedOthers = sortOutcomes(
      Object.fromEntries(otherEntries),
      isOutright,
      marketType,
      isLiveViewOnly,
    );

    return [...sortedPairs, ...sortedOthers];
  }

  return sortOutcomes(outcomes, isOutright, marketType, isLiveViewOnly);
};

const getSortedOutcomes = (
  outcomes: ProcessedOutcomes,
  marketType: string | undefined,
  isOutright: boolean,
  isLiveViewOnly: boolean,
  showInactive: boolean,
): ProcessedOutcome[] => {
  if (marketType === undefined) return [];

  return sortOutcomesForDisplay(
    outcomes,
    marketType,
    isOutright,
    isLiveViewOnly,
    showInactive,
  ).filter((outcome) => (isOutright ? outcome.active : true));
};

// the fallback layout: every outcome in a wrapping grid, two per row unless full width
const StackedOutcomes = ({
  outcomes,
  competitors,
  eventId,
  eventStatus,
  marketId,
  oddsFormat,
  selections,
  toggleSelection,
  className,
  isSuspended = false,
  isOutright = false,
  status,
  isLoading = false,
  mainMarket,
  liftTeamNames = false,
  marketType,
  fullWidth = false,
  renderFieldsKey,
}: StackedOutcomesProps) => {
  const isLiveViewOnly = useIsLiveViewOnly(eventStatus);
  // Settled or view-only shows every line
  const showInactive = isLiveViewOnly || eventStatus === "SETTLED";

  const sortedOutcomes = useMemo(
    () =>
      getSortedOutcomes(
        outcomes,
        marketType,
        isOutright,
        isLiveViewOnly,
        showInactive,
      ),
    [outcomes, marketType, isOutright, isLiveViewOnly, showInactive],
  );

  // full width is a one-column grid
  const columns = !isOutright && !fullWidth ? 2 : 1;
  const count = sortedOutcomes.length;
  const lastRowSize = count % columns || columns;
  const lastRowStart = count - lastRowSize;

  return (
    <>
      {liftTeamNames && (
        <LiftedTeamNames
          outcomes={outcomes}
          competitors={competitors}
          mainMarket={mainMarket}
        />
      )}

      <div className={cx(styles.outcomes, className)}>
        {sortedOutcomes.map((outcome, index) => {
          const isEven = index % 2 === 0;
          const isLast = index === count - 1;
          // outrights style their own corners. A short last row leaves
          // gaps, so tiles above a gap draw their own bottom
          const edges = isOutright
            ? undefined
            : {
                roundBottomLeft: index === lastRowStart,
                roundBottomRight: isLast && lastRowSize === columns,
                dividerRight: index % columns < columns - 1,
                bottomBorder: index < lastRowStart && index + columns >= count,
              };

          return (
            <Outcome
              key={`outcome-${outcome.id}`}
              className={cx(styles.outcome, {
                [styles.outrightOutcome]: isOutright,
                // alone on a short last row
                [styles.halfWidth]: isLast && lastRowSize < columns,
              })}
              align={isOutright ? "left" : isEven ? "right" : "left"}
              id={outcome.id}
              marketId={marketId}
              eventId={eventId}
              eventStatus={eventStatus}
              outcome={outcome}
              competitor={getOutcomeCompetitor(
                competitors,
                outcome,
                mainMarket,
              )}
              toggleSelection={toggleSelection}
              isSuspended={isSuspended || !outcome.active}
              isOutright={isOutright}
              isSelected={selections.includes(outcome.id)}
              noTopBorder={liftTeamNames && index < 2}
              fullWidth={fullWidth}
              edges={edges}
              oddsFormat={oddsFormat}
              status={status}
              isLoading={isLoading}
              renderFieldsKey={renderFieldsKey}
            />
          );
        })}
      </div>
    </>
  );
};

export { StackedOutcomes };
